/**
 * UpgradePrompt — CTA reutilizable para cuando una feature está bloqueada por plan.
 *
 * Se alimenta del body 403 del feature gate del backend:
 *   { feature, requiredPlan, availableAsAddOn, addOnFromPlan, upgrade }
 *
 * Decisión de CTA:
 *  - Si la feature se puede comprar como add-on desde el plan actual
 *    (availableAsAddOn && addOnFromPlan <= currentPlan < requiredPlan):
 *      muestra "Activar Add-on (USD X/mes)".
 *      Si recibe `onActivateAddOn`, lo invoca inline; si no, navega a /subscription.
 *  - Si no: muestra "Actualizar a plan {requiredPlan}" y navega a /plan-selection.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, Sparkles, ArrowUpCircle } from 'lucide-react';
import './UpgradePrompt.css';

const PLAN_ORDER = { free: 1, starter: 2, professional: 3, business: 4, enterprise: 5 };
const planLevel = (planId) => PLAN_ORDER[planId] || 0;

function UpgradePrompt({
  feature,
  requiredPlan,
  availableAsAddOn = false,
  addOnFromPlan,
  currentPlan,
  addOnPrice,
  currency = 'USD',
  onActivateAddOn,
  activating = false,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const featureName = t(`upgrade.features.${feature}`, feature);
  const planName = t(`planSelection.plans.${requiredPlan}.name`, requiredPlan);

  const canActivateAddOn =
    availableAsAddOn &&
    !!currentPlan &&
    !!addOnFromPlan &&
    planLevel(currentPlan) >= planLevel(addOnFromPlan) &&
    planLevel(currentPlan) < planLevel(requiredPlan);

  const symbol = currency === 'ARS' ? 'AR$' : currency === 'BRL' ? 'R$' : '$';
  const priceLabel =
    addOnPrice != null ? `${symbol}${addOnPrice}/${t('subscription.month')}` : null;

  const handleAddOnClick = () => {
    if (onActivateAddOn) {
      onActivateAddOn();
    } else {
      navigate('/subscription');
    }
  };

  return (
    <div className="upgrade-prompt" role="region" aria-label={t('upgrade.featureNotAvailable')}>
      <div className="upgrade-prompt-icon">
        {canActivateAddOn ? <Sparkles size={32} /> : <Lock size={32} />}
      </div>

      <h3 className="upgrade-prompt-title">{t('upgrade.featureNotAvailable')}</h3>

      <p className="upgrade-prompt-text">
        {canActivateAddOn
          ? t('upgrade.addOnAvailable', { feature: featureName })
          : t('upgrade.requiresPlan', { feature: featureName, plan: planName })}
      </p>

      {canActivateAddOn ? (
        <button
          type="button"
          className="btn btn-primary upgrade-prompt-cta"
          onClick={handleAddOnClick}
          disabled={activating}
        >
          <Sparkles size={18} />
          {activating
            ? t('upgrade.activating')
            : priceLabel
              ? t('upgrade.activateAddOnPriced', { feature: featureName, price: priceLabel })
              : t('upgrade.activateAddOn', { feature: featureName })}
        </button>
      ) : (
        <button
          type="button"
          className="btn btn-primary upgrade-prompt-cta"
          onClick={() => navigate('/plan-selection')}
        >
          <ArrowUpCircle size={18} />
          {t('upgrade.upgradeToPlan', { plan: planName })}
        </button>
      )}
    </div>
  );
}

export default UpgradePrompt;
